/**
 * The editor's undo history (0.96.0), pure so it can be tested: snapshots of the document and the sequence's
 * name. An edit can carry a coalescing key (a slider being dragged, a field being typed in): edits with the same
 * key that follow each other closely make one undo step, so undo takes back the whole gesture or field edit.
 * `seal()` (pointer up, leaving a field) ends a gesture early.
 */

export const COALESCE_MS = 1500;
export const MAX_UNDO = 200;

export class History<T> {
  private past: T[] = [];
  private future: T[] = [];
  private lastKey: string | null = null;
  private lastAt = 0;

  /**
   * Record that `before` is about to be replaced by a new state. With `key`, an edit right after another with the
   * same key (within COALESCE_MS, no seal in between) adds no step: undo goes back to before the first of them.
   */
  record(before: T, key?: string, now = Date.now()): void {
    const joins = key != null && key === this.lastKey && now - this.lastAt <= COALESCE_MS;
    this.lastKey = key ?? null;
    this.lastAt = now;
    this.future = [];
    if (joins) return;
    this.past.push(before);
    if (this.past.length > MAX_UNDO) this.past.shift();
  }

  /** The state to go back to (and remember `current` for redo), or null when there is nothing to undo. */
  undo(current: T): T | null {
    const prev = this.past.pop();
    if (prev === undefined) return null;
    this.future.push(current);
    this.seal();
    return prev;
  }

  redo(current: T): T | null {
    const next = this.future.pop();
    if (next === undefined) return null;
    this.past.push(current);
    this.seal();
    return next;
  }

  /** The current gesture is over: the next edit is a new step even with the same key. */
  seal(): void {
    this.lastKey = null;
  }

  clear(): void {
    this.past = [];
    this.future = [];
    this.lastKey = null;
  }

  get canUndo(): boolean { return this.past.length > 0; }
  get canRedo(): boolean { return this.future.length > 0; }
}
